import { Reducer } from "redux"
import { DirectionX, DirectionY, KeyboardBehavior, Strategy } from "./types"
import { sortByAppearanceInDomTree } from "../../core/helpers/webcomponent-helpers"
import { add, remove } from "../../core/helpers/immutability-helpers"
import { Display } from "../../core/types/types"

export interface SelectState {
  selection: HTMLSdxSelectOptionElement[]
  multiple: boolean
  directionX: DirectionX
  directionY: DirectionY
  keyboardBehavior: KeyboardBehavior
  listEl?: HTMLSdxSelectListElement
  sort: boolean
  optionEls: HTMLSdxSelectOptionElement[]
  optgroupEls: HTMLSdxSelectOptgroupElement[]
  name?: string
  filter: string
  filterable: boolean
  filterInputElValue: string
  filterInputElPlaceholder: string
  filterMinLength: number
  noMatchesFound: boolean
  noMatchesFoundLabel: string
  animationDuration: number
  reappending: boolean
  reappendedItems: number
  connected: boolean
  userInteractionInProgress: boolean
  placeholder: string
  required: boolean
  display: Display
  loading: boolean
  close(): Promise<void>
  isInsideDialog: boolean
}

export type SelectActions =
  | {
      type: "SELECT"
      optionEl: HTMLSdxSelectOptionElement | null
      strategy: Strategy
    }
  | { type: "ACTIVATE_SORT" }
  | { type: "ADD_OPTION_EL"; optionEl: HTMLSdxSelectOptionElement }
  | { type: "REMOVE_OPTION_EL"; optionEl: HTMLSdxSelectOptionElement }
  | {
      type: "ADD_OPTGROUP_EL"
      optgroupEl: HTMLSdxSelectOptgroupElement
    }
  | {
      type: "REMOVE_OPTGROUP_EL"
      optgroupEl: HTMLSdxSelectOptgroupElement
    }
  | { type: "SET_FILTER"; filter: SelectState["filter"] }
  | {
      type: "SET_VALIDATED_FILTER_INPUT_EL_VALUE"
      filterInputElValue: SelectState["filterInputElValue"]
    }
  | { type: "UPDATE_REAPPENDING" }

/**
 * Get the text that will be displayed in the selection header.
 * Fall back to an empty string if there's no selection.
 */
export function getFormattedSelection(
  selection: HTMLSdxSelectOptionElement[]
): string {
  return selection.length ? selection.map(getDisplayText).join(", ") : ""
}

/**
 * Checks if a given "keyboard-behavior" is active, including backwards
 * compatibility with the deprecated "filterable" prop.
 * @param store Context with store properties.
 * @param keyboardBehavior Behavior to test.
 */
export function isKeyboardBehavior(
  store: SelectState,
  keyboardBehavior: KeyboardBehavior
): boolean {
  const isMatch = keyboardBehavior === store.keyboardBehavior

  if (keyboardBehavior === "filter" && (isMatch || store.filterable)) {
    return true
  }

  return isMatch
}

export function isFilterLongEnough(
  filter: string,
  filterMinLength: number
): boolean {
  return filter.length > 0 && filter.length >= filterMinLength
}

export function isValidFilter(
  filter: string,
  selection: HTMLSdxSelectOptionElement[],
  filterMinLength: number
): boolean {
  return (
    isFilterLongEnough(filter, filterMinLength) &&
    filter !== getFormattedSelection(selection) // Ignore selection as filter
  )
}

export function getDisplayText(optionEl: HTMLSdxSelectOptionElement): string {
  return optionEl.displayText || optionEl.textContent?.trim() || ""
}

export const selectReducer: Reducer<SelectState, SelectActions> = (
  state = {} as SelectState,
  action
): SelectState => {
  switch (action.type) {
    case "SELECT":
      let { selection } = state

      if (action.optionEl) {
        // selecting a "real" option (not the placeholder)
        if (state.multiple) {
          // multi-select
          const alreadySelected = selection.includes(action.optionEl)

          if (alreadySelected || action.strategy === "remove") {
            // remove from selection
            selection = selection.filter(
              (optionEl) => optionEl !== action.optionEl
            )
          } else {
            // "add" to selection
            selection = [...selection, action.optionEl].sort(
              sortByAppearanceInDomTree
            )
          }
        } else {
          // single select
          const alreadySelected = selection[0] === action.optionEl

          if (alreadySelected) {
            if (action.strategy === "remove") {
              selection = []
            }
          } else {
            if (action.strategy === "add") {
              selection = [action.optionEl]
            }
          }
        }
      } else {
        // selecting the placeholder
        if (selection.length) {
          // Only create a new array if the selection isn't empty
          // to prevent unnecessary change detection.
          selection = []
        }
      }

      return { ...state, selection }

    case "ACTIVATE_SORT":
      return {
        ...state,
        sort: true,
        optionEls: [...state.optionEls].sort(sortByAppearanceInDomTree),
      }

    case "ADD_OPTION_EL":
      return {
        ...state,
        optionEls: state.sort
          ? add(state.optionEls, action.optionEl).sort(
              sortByAppearanceInDomTree
            )
          : add(state.optionEls, action.optionEl),
      }

    case "REMOVE_OPTION_EL":
      return {
        ...state,
        optionEls: state.sort
          ? remove(state.optionEls, action.optionEl).sort(
              sortByAppearanceInDomTree
            )
          : remove(state.optionEls, action.optionEl),
      }

    case "ADD_OPTGROUP_EL":
      return {
        ...state,
        optgroupEls: state.sort
          ? add(state.optgroupEls, action.optgroupEl).sort(
              sortByAppearanceInDomTree
            )
          : add(state.optgroupEls, action.optgroupEl),
      }

    case "REMOVE_OPTGROUP_EL":
      return {
        ...state,
        optgroupEls: state.sort
          ? remove(state.optgroupEls, action.optgroupEl).sort(
              sortByAppearanceInDomTree
            )
          : remove(state.optgroupEls, action.optgroupEl),
      }

    case "SET_FILTER":
      return {
        ...state,
        filter: isValidFilter(
          action.filter,
          state.selection,
          state.filterMinLength
        )
          ? action.filter
          : "",
      }

    case "SET_VALIDATED_FILTER_INPUT_EL_VALUE":
      return {
        ...state,
        filterInputElValue: isValidFilter(
          action.filterInputElValue,
          state.selection,
          state.filterMinLength
        )
          ? action.filterInputElValue
          : "",
      }

    case "UPDATE_REAPPENDING":
      const reappendedItems = state.reappendedItems + 1
      const reappending =
        reappendedItems !== state.optionEls.length + state.optgroupEls.length

      return {
        ...state,
        reappending,
        reappendedItems: reappending ? reappendedItems : 0,
      }

    default:
      return state
  }
}

export function getInitialState(): SelectState {
  return {
    selection: [],
    multiple: false,
    directionX: "right",
    directionY: "bottom",
    keyboardBehavior: "focus",
    listEl: undefined,
    sort: false,
    optionEls: [],
    optgroupEls: [],
    name: undefined,
    filter: "",
    filterable: false,
    filterInputElValue: "",
    filterInputElPlaceholder: "",
    filterMinLength: 3,
    noMatchesFound: false,
    noMatchesFoundLabel: "",
    animationDuration: 0,
    reappending: false,
    reappendedItems: 0,
    connected: false,
    userInteractionInProgress: false,
    placeholder: "",
    required: false,
    display: "closed",
    loading: false,
    close: () => Promise.resolve(),
    isInsideDialog: false,
  }
}
